import React, { useEffect, useState } from 'react';
import {
  CardBackground,
  CardSection,
  CardMiddle,
  CardPaymentPlan,
  CardPaymentRow,
  Circle,
  SectionCard,
  SectionCardSelect,
  SelectDaysPayment,
  SelectSelectWrapper,
  SimulationRange,
  LineSeparator,
  WarningWrapperPlan,
  WrapperTextInfo,
  QuantityTextInfo,
  DescriptionText,
  SimulationTerm,
} from './PaymentCardStyledComponents';
import { colors } from '../../../../../styles/Colors';
import {
  TextBold,
  TextMedium,
  TextRegular,
} from '../../../../../styles/GlobalStyledComponents';
import { pricesHelper } from '../../../../../utils/paymentsHelper';
import { SimulationAmount } from '../../../../CardInputs/CardsStyledComponents';
import Timeline from '../../../../TimeLine/TimeLine';
import { DetailPaymentPlan } from '../../../../DetailPaymentPlan';
import IconInformationRedPlan from '../../../../../assets/icons/IconInformationRedPlan';
import { DetailOverlay } from '../../../../CustomerHeader/CustomerHeaderStyledComponents';
import { SimulationData } from '../../../../../interfaces/PaymentPlan';
import {
  addMonthsToDate,
  getLastDayOfMonth,
  getLocalDateString,
  getNewDate,
  getNextDateWithMonthDay,
  getTodayDayOfMonth,
  todayDateFormatted,
} from '../../../../../utils/dateTimeHelper';

interface PaymentPlanProps {
  totalDebtAmount: number;
  simulationData: SimulationData[];
  handleMonthDaySelected: (value: number) => void;
  calculateNewSimulation: (value: number) => void;
}

const PaymentPlan: React.FC<PaymentPlanProps> = ({
  totalDebtAmount,
  simulationData,
  handleMonthDaySelected,
  calculateNewSimulation,
}) => {
  const [modalOpened, setmodalOpened] = useState<boolean>(false);
  const [minAvailableTerm, setMinAvailableTerm] = useState<number>(0);
  const [maxAvailableTerm, setMaxAvailableTerm] = useState<number>(0);
  const [term, setTerm] = useState<number>(0);
  const [monthDay, setMonthDay] = useState<number>(1);
  const [amountSelectedInput, setAmountSelectedInput] = useState<number>(0);
  const [datesList, setDatesList] = useState<string[]>([]);
  const limitDay = getLastDayOfMonth();

  const initSimulation = (data: SimulationData[]) => {
    // Valores del simulateRange
    const minAvailable = Math.min(...data.map((item) => item.installment));
    // const maxAvailable = Math.max(...data.map((item) => item.installment));

    // Plazos para pintar el mínimo y máximo
    const minTerm = Math.min(...data.map((item) => item.term));
    const maxTerm = Math.max(...data.map((item) => item.term));
    setMinAvailableTerm(minTerm);
    setMaxAvailableTerm(maxTerm);

    // Valor intermedio del simulateRange basado en term
    const midTerm = Math.floor((minTerm + maxTerm) / 2);
    setTerm(midTerm);

    // Valor inicial del simulateRange
    const initialInstallment =
      data.find((item) => item.term === midTerm)?.installment || minAvailable;
    setAmountSelectedInput(initialInstallment);

    // Valores por defecto para calcular el pago inicial con el plazo intermedio y el día actual del mes para empezar a pagar
    calculateNewSimulation(midTerm);
    const currentDay = getTodayDayOfMonth();
    getDatesList(currentDay, midTerm);
  };

  const getDatesList = (day: number, term: number) => {
    if (day > 0 && term > 0) {
      // La siguiente fecha en la que tendrá que pagar:
      let futureDate = addMonthsToDate(getNextDateWithMonthDay(day), 1);

      // Si no se corresponde habrá que ajustarla
      if (futureDate.getDate() !== day && day >= 29) {
        futureDate = getNewDate(futureDate, day, 1); // Ajusta al día seleccionado
      }
      setDatesList([todayDateFormatted, getLocalDateString(futureDate)]); // Actualiza la lista de fechas
      handleMonthDaySelected(day); // Envía el número del día que haya elegido
      setMonthDay(day); // Establece el día actual como valor por defecto
    }
  };

  useEffect(() => {
    // Cada vez que se actualizan los datos de simulación, se ajusta su valor máximo, mínimo y el salto de uno a otro
    if (simulationData && simulationData.length > 0) {
      initSimulation(simulationData);
    } else {
      console.log('No hay datos de simulación disponibles');
    }
  }, [simulationData]);

  return (
    <>
      <DetailOverlay
        open={modalOpened}
        onClick={() => setmodalOpened(false)}
        id="payment-detail-modal-background-close"
      />
      <CardPaymentPlan id="co-custom-payment-amount-section">
        <SectionCard id="co-custom-card-section">
          <CardMiddle id="co-custom-card-middle">
            <Circle>1</Circle>
          </CardMiddle>
          <CardMiddle id="co-custom-card-middle">
            <CardPaymentRow
              selected={true}
              borderColor={colors.red8}
              backgroundColor={colors.gray12}
              cardPadding="5px 10px"
              id="co-custom-payment-plan-card-row"
            >
              <TextMedium fontSize={12} lineHeight={16} fontWeight={500}>
                Pendiente{' '}
                <TextMedium fontSize={12} lineHeight={16} fontWeight={700}>
                  {pricesHelper(totalDebtAmount)} €
                </TextMedium>
              </TextMedium>
            </CardPaymentRow>
          </CardMiddle>
        </SectionCard>
        <CardSection id="co-custom-card-section">
          <TextMedium fontSize={14} lineHeight={20} fontWeight={500}>
            Selecciona en cuantos meses te gustaría pagar la deuda
          </TextMedium>
        </CardSection>
        <SectionCard id="co-custom-card-section">
          <CardMiddle id="co-custom-card-middle">
            <SimulationAmount
              fontSize={28}
              fontWeight={700}
              lineHeight={36}
              id="simulation-amount"
            >
              {amountSelectedInput && pricesHelper(amountSelectedInput)} €/mes
            </SimulationAmount>
          </CardMiddle>
          <CardMiddle id="co-custom-card-middle">
            <SimulationTerm
              fontSize={18}
              fontWeight={700}
              lineHeight={24}
              id="months-selected"
            >
              {term} meses
            </SimulationTerm>
          </CardMiddle>
        </SectionCard>
        <SimulationRange
          type="range"
          id="simulation-range"
          min={minAvailableTerm}
          max={maxAvailableTerm}
          step={1}
          value={term}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            // Asegura que sea un número y comprueba si el valor se encuentra en el rango
            const newValue = simulationData.find(
              (item) =>
                item.term === parseFloat(Number(event.target.value).toFixed(2))
            );
            if (newValue) {
              setAmountSelectedInput(newValue?.installment || 0); // Actualiza el importe
              calculateNewSimulation(newValue.term); // Envía el valor de term al padre
              setTerm(newValue.term || 0); // Sitúa en el nuevo rango el valor
              getDatesList(monthDay, newValue.term); // Llama a la función para obtener las fechas actualizadas
            }
          }}
        />
        <SectionCard id="co-custom-card-section">
          <CardMiddle id="co-custom-card-middle">
            <TextMedium
              fontSize={14}
              fontWeight={400}
              lineHeight={20}
              id="months-selected"
            >
              {minAvailableTerm} meses
            </TextMedium>
          </CardMiddle>
          <CardMiddle id="co-custom-card-middle">
            <TextMedium
              fontSize={14}
              fontWeight={400}
              lineHeight={20}
              id="months-total-id"
            >
              {maxAvailableTerm} meses
            </TextMedium>
          </CardMiddle>
        </SectionCard>
        <LineSeparator />
        <CardSection id="co-custom-card-section">
          <Circle>2</Circle>
        </CardSection>
        <CardSection id="co-custom-card-section">
          <TextMedium fontSize={14} lineHeight={20} fontWeight={500}>
            Establece el día de pago
          </TextMedium>
        </CardSection>
        <SelectSelectWrapper>
          <SectionCardSelect id="co-custom-card-section">
            <CardMiddle id="co-custom-card-middle">
              <SelectDaysPayment
                value={monthDay}
                id="amount-input"
                name="amount"
                inputMode="numeric"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const selectedValue = parseInt(e.target.value);
                  getDatesList(selectedValue, term); // Llama a la función para obtener las fechas
                }}
                autoComplete="off"
              >
                {[...Array(limitDay)].map((_, index) => (
                  <option key={index} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </SelectDaysPayment>
            </CardMiddle>
            <CardMiddle id="co-custom-card-middle">
              <TextMedium fontSize={14} lineHeight={22}>
                De cada mes
              </TextMedium>
            </CardMiddle>
          </SectionCardSelect>
        </SelectSelectWrapper>
        <CardBackground id="co-custom-card-background">
          <DescriptionText id="co-custom-card-background-text">
            <TextMedium
              id="co-custom-card-background-text"
              fontSize={14}
              lineHeight={22}
              fontWeight={500}
              color="gray6"
            >
              <TextBold fontSize={14} lineHeight={22} color="gray6">
                {' '}
                Hoy
              </TextBold>{' '}
              deberás abonar tu primera cuota de{' '}
              <QuantityTextInfo
                fontSize={14}
                lineHeight={22}
                fontWeight={700}
                color="gray6"
              >
                {amountSelectedInput && pricesHelper(amountSelectedInput)} €
              </QuantityTextInfo>
            </TextMedium>
          </DescriptionText>
          <Timeline amountSimulated={amountSelectedInput} dates={datesList} />
        </CardBackground>
        <WarningWrapperPlan
          id="payment-plan-warning"
          onClick={() => setmodalOpened(true)}
        >
          <IconInformationRedPlan fontWeight={700} fontSize={14} />
          <WrapperTextInfo>
            <TextRegular
              fontSize={14}
              color={'red6'}
              fontWeight={700}
              lineHeight={20}
            >
              Ver todas las cuotas
            </TextRegular>
          </WrapperTextInfo>
        </WarningWrapperPlan>
      </CardPaymentPlan>

      {!!(modalOpened && term && amountSelectedInput && monthDay) && (
        <DetailPaymentPlan
          close={() => setmodalOpened(false)}
          simulationDetailData={{
            term: term,
            installment: amountSelectedInput,
            month_day: monthDay,
          }}
        />
      )}
    </>
  );
};

export default PaymentPlan;
